import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CalendarIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PhoneIcon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import axiosInstance from '../../utils/axios';
import API_URLS from '../../config/api';

const statusStyles = {
  pending: 'bg-amber-100 text-amber-800',
  accepted: 'bg-blue-100 text-blue-800',
  confirmed: 'bg-indigo-100 text-indigo-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  declined: 'bg-gray-100 text-gray-700',
};

const getName = (user) => {
  if (!user) return '';
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || user.email || '';
};

const getDriver = (booking) => booking.driver || booking.ride?.driver || null;

const getPickupAddress = (booking) => {
  if (booking.pickupStreet || booking.pickupCity || booking.pickupPostcode) {
    return [
      booking.pickupStreet,
      [booking.pickupCity, booking.pickupPostcode].filter(Boolean).join(' '),
    ].filter(Boolean).join(', ');
  }
  return booking.pickupLocation || 'Not provided';
};

const formatDate = (value) => {
  if (!value) return 'Not scheduled';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return 'Not scheduled';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatStatus = (status) => {
  if (!status) return 'Unknown';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const escapeCsvCell = (value) => {
  const text = String(value ?? '');
  const safeText = /^[\s]*[=+\-@]/.test(text) ? `'${text}` : text;
  return `"${safeText.replace(/"/g, '""')}"`;
};

function BookingDetails({ booking, onClose }) {
  const driver = getDriver(booking);
  const status = String(booking.status || '').toLowerCase();

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-details-title"
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl"
      >
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 id="booking-details-title" className="text-xl font-semibold text-gray-900">
              Booking #{booking.id}
            </h2>
            <span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
              {formatStatus(status)}
            </span>
          </div>
          <button type="button" onClick={onClose} aria-label="Close booking details" className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="grid gap-6 px-6 py-5 sm:grid-cols-2">
          <div>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Rider</h3>
            <p className="font-medium text-gray-900">{getName(booking.rider) || 'Unknown rider'}</p>
            <p className="mt-1 text-sm text-gray-600">{booking.rider?.email || 'Email not provided'}</p>
            {booking.rider?.phone ? (
              <a href={`tel:${booking.rider.phone}`} className="mt-1 inline-flex items-center gap-1 text-sm text-indigo-700 hover:text-indigo-900">
                <PhoneIcon className="h-4 w-4" />{booking.rider.phone}
              </a>
            ) : (
              <p className="mt-1 text-sm text-gray-600">Phone not provided</p>
            )}
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Driver</h3>
            {driver ? (
              <>
                <p className="font-medium text-gray-900">{getName(driver) || 'Assigned driver'}</p>
                <p className="mt-1 text-sm text-gray-600">{driver.email || 'Email not provided'}</p>
                {driver.phone ? (
                  <a href={`tel:${driver.phone}`} className="mt-1 inline-flex items-center gap-1 text-sm text-indigo-700 hover:text-indigo-900">
                    <PhoneIcon className="h-4 w-4" />{driver.phone}
                  </a>
                ) : (
                  <p className="mt-1 text-sm text-gray-600">Phone not provided</p>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-600">No driver assigned</p>
            )}
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Request</h3>
            <p className="flex items-start gap-2 text-sm text-gray-800">
              <MapPinIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
              <span><span className="font-medium">Pickup:</span> {getPickupAddress(booking)}</span>
            </p>
            <p className="mt-2 text-sm text-gray-800"><span className="font-medium">Destination:</span> {booking.destination || booking.ride?.dropoffLocation || 'Not provided'}</p>
            <p className="mt-2 flex items-center gap-2 text-sm text-gray-800">
              <CalendarIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
              {formatDate(booking.date)}{(booking.serviceSession || booking.serviceTime) && ` · ${booking.serviceSession || ''}${booking.serviceTime ? ` ${booking.serviceTime}` : ''}`}
            </p>
            <p className="mt-2 text-sm text-gray-800">Passengers / seats: {booking.passengers || booking.seats || 'Not specified'}</p>
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Ride details</h3>
            {booking.ride ? (
              <>
                <p className="text-sm text-gray-800">Ride #{booking.ride.id}</p>
                <p className="mt-1 text-sm text-gray-600">
                  {booking.ride.pickupLocation || 'Pickup not specified'} to {booking.ride.dropoffLocation || 'destination not specified'}
                </p>
                <p className="mt-1 text-sm text-gray-600">Departure: {booking.ride.time || 'Not specified'}</p>
              </>
            ) : (
              <p className="text-sm text-gray-600">Not linked to a posted ride</p>
            )}
          </div>

          {booking.specialRequests && (
            <div className="sm:col-span-2">
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">Special requests</h3>
              <p className="whitespace-pre-wrap text-sm text-gray-800">{booking.specialRequests}</p>
            </div>
          )}
        </div>

        <div className="flex justify-end border-t border-gray-200 px-6 py-4">
          <button type="button" onClick={onClose} className="rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
            Close
          </button>
        </div>
      </section>
    </div>
  );
}

function BookingsManagement() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [driverFilter, setDriverFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axiosInstance.get(API_URLS.admin.bookings);
      if (!Array.isArray(response.data)) {
        throw new Error('The bookings response was not a list.');
      }
      setBookings(response.data);
      setSelectedBooking((current) => (
        current ? response.data.find((booking) => booking.id === current.id) || null : null
      ));
    } catch (err) {
      console.error('Error fetching admin bookings:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load bookings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const statuses = useMemo(() => (
    [...new Set(bookings.map((booking) => String(booking.status || '').toLowerCase()).filter(Boolean))].sort()
  ), [bookings]);

  const filteredBookings = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return bookings.filter((booking) => {
      const status = String(booking.status || '').toLowerCase();
      const hasDriver = Boolean(booking.driverId || getDriver(booking)?.id || booking.ride?.driverId);
      const bookingDate = booking.date || '';

      if (selectedStatus !== 'all' && status !== selectedStatus) return false;
      if (driverFilter === 'assigned' && !hasDriver) return false;
      if (driverFilter === 'unassigned' && hasDriver) return false;
      if (fromDate && (!bookingDate || bookingDate < fromDate)) return false;
      if (toDate && (!bookingDate || bookingDate > toDate)) return false;
      if (!term) return true;

      return [
        booking.id,
        getName(booking.rider),
        booking.rider?.email,
        booking.rider?.phone,
        getPickupAddress(booking),
        booking.destination,
        getName(getDriver(booking)),
        getDriver(booking)?.phone,
      ].some((value) => String(value || '').toLowerCase().includes(term));
    });
  }, [bookings, searchTerm, selectedStatus, driverFilter, fromDate, toDate]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedStatus('all');
    setDriverFilter('all');
    setFromDate('');
    setToDate('');
  };

  const exportCsv = () => {
    const headers = [
      'Booking ID', 'Status', 'Rider', 'Rider Email', 'Rider Phone',
      'Pickup Address', 'Destination', 'Date', 'Service', 'Service Time',
      'Passengers', 'Driver', 'Driver Email', 'Driver Phone', 'Ride ID', 'Special Requests',
    ];
    const rows = filteredBookings.map((booking) => {
      const driver = getDriver(booking);
      return [
        booking.id,
        booking.status,
        getName(booking.rider) || 'Unknown rider',
        booking.rider?.email,
        booking.rider?.phone,
        getPickupAddress(booking),
        booking.destination || booking.ride?.dropoffLocation,
        booking.date,
        booking.serviceSession || booking.ride?.serviceSession,
        booking.serviceTime || booking.ride?.time,
        booking.passengers || booking.seats,
        getName(driver),
        driver?.email,
        driver?.phone,
        booking.rideId || booking.ride?.id,
        booking.specialRequests,
      ];
    });
    const csv = [headers, ...rows].map((row) => row.map(escapeCsvCell).join(',')).join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bookings-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Bookings Management</h1>
          <p className="mt-2 text-sm text-gray-700">Review all ride bookings, filter the list, and inspect booking and participant details.</p>
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={fetchBookings} disabled={loading} className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
            <ArrowPathIcon className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh
          </button>
          <button type="button" onClick={exportCsv} disabled={filteredBookings.length === 0} className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50">
            <ArrowDownTrayIcon className="mr-2 h-4 w-4" />Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" className="mb-6 flex items-center justify-between rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <span>{error}</span>
          <button type="button" onClick={fetchBookings} className="ml-4 font-medium underline">Try again</button>
        </div>
      )}

      <div className="mb-6 rounded-lg bg-white p-4 shadow">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
          <div className="relative md:col-span-2 xl:col-span-2">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search rider, phone, address, driver, or ID"
              aria-label="Search bookings"
              className="block w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <select aria-label="Filter by status" value={selectedStatus} onChange={(event) => setSelectedStatus(event.target.value)} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
            <option value="all">All statuses</option>
            {statuses.map((status) => <option key={status} value={status}>{formatStatus(status)}</option>)}
          </select>
          <select aria-label="Filter by driver assignment" value={driverFilter} onChange={(event) => setDriverFilter(event.target.value)} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
            <option value="all">Any assignment</option>
            <option value="assigned">Driver assigned</option>
            <option value="unassigned">No driver assigned</option>
          </select>
          <label className="text-xs font-medium text-gray-600">
            From
            <input type="date" aria-label="Filter bookings from date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
          </label>
          <label className="text-xs font-medium text-gray-600">
            To
            <input type="date" aria-label="Filter bookings to date" value={toDate} onChange={(event) => setToDate(event.target.value)} className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
          </label>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-gray-600">Showing {filteredBookings.length} of {bookings.length} bookings</p>
          <button type="button" onClick={clearFilters} className="text-sm font-medium text-indigo-700 hover:text-indigo-900">Clear filters</button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-indigo-600" aria-label="Loading bookings" />
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-sm font-medium text-gray-900">{bookings.length ? 'No bookings match the selected filters.' : 'No bookings found.'}</p>
            <p className="mt-1 text-sm text-gray-500">{bookings.length ? 'Adjust or clear the filters to see more bookings.' : 'Bookings will appear here when they are submitted.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Booking', 'Rider', 'Pickup', 'Date / service', 'Driver', 'Status', ''].map((heading, index) => (
                    <th key={`${heading}-${index}`} scope="col" className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredBookings.map((booking) => {
                  const driver = getDriver(booking);
                  const status = String(booking.status || '').toLowerCase();
                  return (
                    <tr key={booking.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-5 py-4">
                        <p className="text-sm font-medium text-gray-900">#{booking.id}</p>
                        <p className="text-xs text-gray-500">{booking.passengers || booking.seats || 1} passenger(s)</p>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <UserCircleIcon className="h-8 w-8 flex-shrink-0 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{getName(booking.rider) || 'Unknown rider'}</p>
                            <p className="text-xs text-gray-500">{booking.rider?.phone || booking.rider?.email || 'No contact details'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="max-w-xs px-5 py-4">
                        <p className="flex items-start gap-1.5 text-sm text-gray-700">
                          <MapPinIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                          <span>{getPickupAddress(booking)}</span>
                        </p>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4">
                        <p className="text-sm text-gray-900">{formatDate(booking.date)}</p>
                        <p className="text-xs text-gray-500">{booking.serviceSession || 'Service not set'}{booking.serviceTime ? ` · ${booking.serviceTime}` : ''}</p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-sm text-gray-900">{getName(driver) || 'Unassigned'}</p>
                        {driver?.phone && <p className="text-xs text-gray-500">{driver.phone}</p>}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
                          {formatStatus(status)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-right">
                        <button type="button" onClick={() => setSelectedBooking(booking)} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-indigo-700 hover:bg-indigo-50 hover:text-indigo-900">
                          <EyeIcon className="h-4 w-4" />Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedBooking && <BookingDetails booking={selectedBooking} onClose={() => setSelectedBooking(null)} />}
    </div>
  );
}

export default BookingsManagement;
